/**
 * What this browser has already been shown of the guided tour.
 *
 * <p>Two different lifetimes, so two different stores. Whether a tour has been seen belongs to the
 * browser for good, like the reports in {@code lib/saved.ts}: a tour that reintroduces itself on
 * every visit is an advertisement. Where a running tour had got to belongs to the tab and no
 * longer, and it exists for one reason - {@code LanguageProvider} remounts the whole tree when the
 * language changes, and the last step of the home tour points at the language switch. Without
 * this, using the very thing the tour just pointed at would throw the reader back to step one.
 *
 * <p>Every accessor tolerates storage being unavailable, because a private window is normal rather
 * than exceptional. Losing this costs a reader a tour they have already seen, not the product.
 */

/** The tours that exist. One per screen that has something worth pointing at. */
export type TourId = 'home' | 'report';

const SEEN_KEY = 'scanhome.tours';
const PLACE_KEY = 'scanhome.tour.place';

function readSeen(): string[] {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((entry): entry is string => typeof entry === 'string');
  } catch {
    // Storage can be unavailable or hold something we did not write; either way, nothing is seen.
    return [];
  }
}

export function hasSeen(id: TourId): boolean {
  return readSeen().includes(id);
}

/** Finishing and skipping both count: a reader who pressed Skip has decided. */
export function markSeen(id: TourId): void {
  try {
    const seen = readSeen();
    if (!seen.includes(id)) {
      window.localStorage.setItem(SEEN_KEY, JSON.stringify([...seen, id]));
    }
  } catch {
    // Not remembered, so the tour will offer itself again next visit. Nothing worse than that.
  }
}

/** Forgets one tour, so the footer's "show it again" has something to show. */
export function forgetSeen(id: TourId): void {
  try {
    window.localStorage.setItem(SEEN_KEY, JSON.stringify(readSeen().filter((entry) => entry !== id)));
  } catch {
    // The tour is started directly by the caller; this only stops it coming back.
  }
}

/** Where a running tour had got to, for the remount that a language change causes. */
export function rememberPlace(id: TourId, step: number): void {
  try {
    window.sessionStorage.setItem(PLACE_KEY, JSON.stringify({ id, step }));
  } catch {
    // A language change mid-tour will restart it from the top. Not worth failing over.
  }
}

/** The remembered step for this tour, or null when there is none for it. */
export function readPlace(id: TourId): number | null {
  try {
    const raw = window.sessionStorage.getItem(PLACE_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    const place = parsed as { id?: unknown; step?: unknown };
    if (place.id !== id || typeof place.step !== 'number' || !Number.isInteger(place.step) || place.step < 0) {
      return null;
    }
    return place.step;
  } catch {
    return null;
  }
}

export function clearPlace(): void {
  try {
    window.sessionStorage.removeItem(PLACE_KEY);
  } catch {
    // Nothing to do.
  }
}
